import { $window } from '@umu/umu-dom';
import { useMemoizedFn } from 'ahooks';
import { type RefObject, useCallback } from 'react';
import { AnnotationsLayer } from '../services/annotationsLayer';
import { renderSvg } from '../services/pdfRenderService';

export interface UseSvgRendererProps {
    scale: number;
    pageNum: number;
    canvasRef: RefObject<HTMLCanvasElement>;
    svgContainerRef?: RefObject<HTMLDivElement>;
    annotationLayerRef?: RefObject<HTMLDivElement>;
    onRenderSuccess?: () => void;
    // biome-ignore lint/suspicious/noExplicitAny: error type
    onRenderError?: (error: any) => void;
    /** Canvas 回退渲染函数（SVG 失败时调用），由 useCanvasRenderer 提供 */
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
    renderByCanvas: (page: any) => Promise<void>;
    // biome-ignore lint/suspicious/noExplicitAny: PDF.js pdfDoc
    pdfDoc?: any;
    onInternalLinkClick?: (pageNum: number) => void;
    /** SVG 与 Canvas 双重失败时触发（供 usePdfRenderer 触发 Image 降级）；
     *  提供此回调时不再调用 onRenderError，避免 Image 重试前出现错误闪现 */
    onSvgFailedCompletely?: () => void;
}

/**
 * SVG 渲染 Hook
 *
 * 从 usePdfRenderer 拆分：封装 SVG 渲染路径，SVG 失败时自动回退到 renderByCanvas。
 */
export function useSvgRenderer(props: UseSvgRendererProps) {
    const {
        scale,
        canvasRef,
        svgContainerRef,
        annotationLayerRef,
        onRenderSuccess,
        onRenderError,
        renderByCanvas,
        pdfDoc,
        onInternalLinkClick,
        onSvgFailedCompletely,
    } = props;

    /** SVG 失败时回退到 Canvas */
    const handleSvgFallback = useMemoizedFn(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        (page: any, isStale: (() => boolean) | undefined) => {
            if (canvasRef.current) canvasRef.current.style.display = 'block';
            if (canvasRef.current && page) {
                if (isStale?.()) return;
                renderByCanvas(page).catch(() => {
                    if (isStale?.()) return;
                    // 若上层提供了 onSvgFailedCompletely，说明还有 Image 降级兜底，
                    // 不再设置 error state（避免在 Image 重试前短暂显示错误 UI）
                    if (onSvgFailedCompletely) {
                        onSvgFailedCompletely();
                    } else {
                        onRenderError?.(new Error('SVG 和 Canvas 渲染均失败'));
                    }
                });
            } else if (onSvgFailedCompletely) {
                onSvgFailedCompletely();
            } else {
                onRenderError?.(new Error('SVG 渲染失败且无法回退到 Canvas'));
            }
        }
    );

    /** SVG 渲染结果回调（提取以降低 renderBySvg 复杂度） */
    const handleSvgRendered = useMemoizedFn(
        (
            // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
            page: any,
            // biome-ignore lint/suspicious/noExplicitAny: PDF.js viewport object
            viewport: any,
            svgContainer: HTMLDivElement,
            isRendered: boolean,
            isStale: (() => boolean) | undefined
        ) => {
            if (isStale?.()) return;
            if (isRendered) {
                if (canvasRef.current) canvasRef.current.style.display = 'none';
                if (annotationLayerRef?.current) {
                    new AnnotationsLayer(
                        $window.pdfjsLib,
                        page,
                        viewport,
                        svgContainer,
                        annotationLayerRef.current,
                        scale,
                        pdfDoc,
                        onInternalLinkClick
                    );
                }
                if (!isStale?.()) onRenderSuccess?.();
            } else {
                handleSvgFallback(page, isStale);
            }
        }
    );

    const renderBySvg = useCallback(
        // biome-ignore lint/suspicious/noExplicitAny: PDF.js page object
        (page: any, isStale?: () => boolean) => {
            const svgContainer = svgContainerRef?.current;
            if (!svgContainer || isStale?.()) return;
            const viewport = page.getViewport({ scale: 1 });
            renderSvg($window.pdfjsLib, page, viewport, svgContainer, (isRendered: boolean) => {
                handleSvgRendered(page, viewport, svgContainer, isRendered, isStale);
            });
        },
        [svgContainerRef, handleSvgRendered]
    );

    return { renderBySvg };
}
